import { createFileRoute } from '@tanstack/react-router';
import { INQUIRY_HREF } from '../../lib/product-content.js';
import React from 'react';
import { PublicLayout } from '../../components/public-layout.js';
import { SolutionLayout } from '../../components/solution-layout.js';
import { SectionBlock } from '../../components/section-block.js';
import type { CrossSellItem } from '../../components/cross-sell.js';

// ─── cross-sell items ─────────────────────────────────────────────────────────
const CROSS_SELL: CrossSellItem[] = [
  {
    href: '/solutions/scst',
    name: 'SCST',
    desc: 'PASCAL-Med 운영 전 단계의 정식 진단보조 도구. 진단 → 중재 흐름의 시작점입니다.',
    stage: 'dx',
  },
  {
    href: '/solutions/amylo',
    name: 'Amylo',
    desc: 'PASCAL-Med 운영 전 영상 기반 정량 확진. ATN의 Amyloid 축을 커버합니다.',
    stage: 'img',
  },
  {
    href: '/solutions/pascal-home',
    name: 'PASCAL-Home',
    desc: '의료기관 안내 후 환자·보호자가 직접 구독하는 비의료기기 가정용 인지 활동 앱.',
    stage: 'int',
  },
];

// ─── annotation placeholder ───────────────────────────────────────────────────
function Anno({ text }: { text: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        background: 'rgba(245,158,11,0.15)',
        borderRadius: 3,
        fontSize: 11,
        color: 'var(--amber-fg-soft)',
        fontWeight: 500,
        marginLeft: 6,
        verticalAlign: 'middle',
      }}
    >
      {text}
    </span>
  );
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────
function Page() {
  return (
    <PublicLayout>
      <SolutionLayout
        stage="int"
        badgeLabel="INT · 인지중재 · 의료기관용"
        name="PASCAL-Med"
        tagline="진단 이후, 의료기관에서 직접 운영할 수 있는 디지털 인지 중재 솔루션."
        domain="의료기관용 · 의료기기 (MZ009 코드 체계)"
        crossSell={CROSS_SELL}
      >

        {/* ── 환자에게 어떻게 작동하는가 ─────────────────────────────────── */}
        <section style={{ padding: '100px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            <SectionBlock
              label="How it works for your patient"
              title="환자에게 어떻게 작동하는가"
              subtitle="진단 후 인지 중재가 필요한 환자에게, PASCAL-Med가 어떻게 사용되고 무엇이 달라지는지 정리했습니다."
            >
              <div className="cols-2 uneven" style={{ gap: 56, alignItems: 'start', marginTop: 64 }}>
                {/* scene card */}
                <div
                  style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--line)',
                    borderRadius: 20,
                    padding: 40,
                    position: 'sticky',
                    top: 80,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: 'rgba(245,158,11,0.1)',
                      border: '1px solid rgba(245,158,11,0.2)',
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24,
                      marginBottom: 20,
                    }}
                  >
                    🩺
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--ink-3)',
                      marginBottom: 8,
                    }}
                  >
                    — USE CASE · 실제 사용 상황
                  </div>
                  <div
                    style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, lineHeight: 1.4, color: 'var(--ink)' }}
                  >
                    진단 후, "이제 무엇을<br />해드려야 할까요?"
                  </div>
                  <div style={{ color: 'var(--ink-2)', lineHeight: 1.7, fontSize: 14 }}>
                    SCST·MMSE로 경도인지장애가 확인된 환자. 가족도 환자도 불안해하지만,
                    의료기관에서 안내할 수 있는 인지 중재 옵션은 제한적이었습니다.
                  </div>
                  <div
                    style={{
                      marginTop: 20,
                      padding: '16px 20px',
                      background: 'rgba(245,158,11,0.06)',
                      borderLeft: '3px solid #f59e0b',
                      borderRadius: '0 8px 8px 0',
                      fontStyle: 'italic',
                      color: 'var(--ink-2)',
                      fontSize: 13,
                      lineHeight: 1.7,
                    }}
                  >
                    "선생님, 진단은 받았는데<br />앞으로 어떻게 하면<br />진행을 늦출 수 있을까요?"
                  </div>
                </div>

                {/* steps */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    {
                      n: 1,
                      title: '의료기관에서 환자 맞춤 인지 훈련 안내',
                      desc: (
                        <>
                          이전 SCST·MMSE 검사 결과가 자동 연동됩니다. 환자의 인지 영역별 점수를 보고{' '}
                          <strong style={{ color: 'var(--ink)' }}>
                            주의력 중심·기억력 중심·실행 기능 중심
                          </strong>{' '}
                          중 적합한 훈련 패키지를 선택합니다.
                        </>
                      ),
                    },
                    {
                      n: 2,
                      title: '환자가 의료기관 태블릿으로 훈련 진행',
                      desc: (
                        <>
                          20~30분 분량의 디지털 인지 훈련. 환자 인지 상태에 따라{' '}
                          <strong style={{ color: 'var(--ink)' }}>난이도가 자동 조정</strong>되어,
                          너무 쉽거나 어렵지 않은 적정 부하를 유지합니다.
                        </>
                      ),
                    },
                    {
                      n: 3,
                      title: '진척도가 의사 대시보드에 자동 기록',
                      desc: (
                        <>
                          매 세션의 점수·반응속도·완수율이 자동으로 의사 대시보드에 쌓입니다.{' '}
                          <strong style={{ color: 'var(--ink)' }}>주차별 변화 그래프</strong>로 환자
                          상태를 한눈에 확인할 수 있습니다.
                        </>
                      ),
                    },
                    {
                      n: 4,
                      title: '가정 인지 활동(PASCAL-Home) 안내',
                      desc: (
                        <>
                          환자·보호자가 집에서 인지 활동을 이어갈 수 있도록 비의료기기 가정용 앱
                          PASCAL-Home을 안내합니다 (구독 주체는 환자·보호자). 의료기관에서는{' '}
                          <strong style={{ color: 'var(--ink)' }}>
                            2~4주 간격 재진료 시 PASCAL-Med로 재검사
                          </strong>
                          하여 변화를 추적합니다.
                        </>
                      ),
                    },
                    {
                      n: 5,
                      title: '정기 추적 데이터로 진료의 연속성 확보',
                      desc: (
                        <>
                          진단 → 중재 → 재검사가 하나의 시스템 안에서 기록됩니다. 환자에게{' '}
                          <strong style={{ color: 'var(--ink)' }}>
                            "지난번보다 좋아지고 있다"
                          </strong>
                          는 객관적 데이터를 보여줄 수 있어 진료 만족도가 높아집니다.
                        </>
                      ),
                    },
                  ].map((s) => (
                    <div
                      key={s.n}
                      style={{
                        background: 'var(--surface-1)',
                        border: '1px solid var(--line)',
                        borderRadius: 14,
                        padding: '24px 28px',
                      }}
                    >
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          background: 'rgba(245,158,11,0.15)',
                          color: 'var(--amber-fg)',
                          borderRadius: '50%',
                          fontFamily: "'Inter Tight', sans-serif",
                          fontWeight: 700,
                          fontSize: 13,
                          marginBottom: 12,
                        }}
                      >
                        {s.n}
                      </div>
                      <div
                        style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, lineHeight: 1.4, color: 'var(--ink)' }}
                      >
                        {s.title}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.7 }}>{s.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 의료기관 가치 ──────────────────────────────────────────────── */}
        <section style={{ padding: '100px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            <SectionBlock label="Value for Your Clinic" title="의료기관에 무엇이 좋은가">
              <div className="cols-3" style={{ gap: 24, marginTop: 56 }}>
                {[
                  {
                    n: '01',
                    title: '진단 이후의 공백을 채웁니다',
                    desc: '치매 진단을 내리고도 "그래서 어떻게 해야 하나"에 답하기 어려웠던 부분. PASCAL-Med는 의료기관에서 직접 안내·운영할 수 있는 인지 중재 도구입니다.',
                  },
                  {
                    n: '02',
                    title: '환자 재방문의 의학적 명분',
                    desc: '진단 한 번으로 끝나지 않습니다. 2~4주 간격 재검사로 환자의 인지 변화를 객관적으로 추적할 수 있어, 진료의 연속성과 환자 만족도가 함께 올라갑니다.',
                  },
                  {
                    n: '03',
                    title: '진료 시간을 늘리지 않습니다',
                    desc: '환자가 태블릿으로 자기 주도적으로 훈련하므로 의사·간호사 인력 부담이 없습니다. 자동 채점·자동 기록으로 결과 정리에 추가 시간이 들지 않습니다.',
                  },
                ].map((c) => (
                  <div
                    key={c.n}
                    style={{
                      background: 'var(--surface-1)',
                      border: '1px solid var(--line)',
                      borderRadius: 16,
                      padding: '32px 28px',
                    }}
                  >
                    <div
                      className="num"
                      style={{ fontSize: 11, color: 'var(--amber-fg)', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 16 }}
                    >
                      — VALUE {c.n}
                    </div>
                    <div
                      style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, lineHeight: 1.4, color: 'var(--ink)' }}
                    >
                      {c.title}
                    </div>
                    <div style={{ color: 'var(--ink-3)', fontSize: 14, lineHeight: 1.7 }}>{c.desc}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 임상 근거 ──────────────────────────────────────────────────── */}
        <section
          id="evidence"
          style={{ padding: '100px 0', borderTop: '1px solid var(--line)' }}
        >
          <div className="container">
            <SectionBlock label="Clinical Evidence" title={<>임상 근거가<br />받쳐주는 솔루션입니다.</>}
              subtitle="디지털 인지 중재의 효과는 국내외 임상 연구로 입증되어 있으며, 인허가와 표준 코드 체계 안에서 운영됩니다."
            >
              <div className="cols-2" style={{ gap: 24, marginTop: 56 }}>
                <div
                  style={{
                    background: 'var(--surface-1)',
                    border: '1px solid var(--line)',
                    borderRadius: 16,
                    padding: 32,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--accent)',
                      marginBottom: 16,
                    }}
                  >
                    — PAPER
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, lineHeight: 1.4, color: 'var(--ink)' }}>
                    디지털 인지 중재의<br />경도인지장애 환자 효과 검증
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', fontStyle: 'italic', marginBottom: 20 }}>
                    국내 학술지 게재 <Anno text="실제 논문 정보로 교체" />
                  </div>
                  <div className="cols-3 tight" style={{ gap: 12 }}>
                    {[
                      { num: '<실제 데이터로 교체>', label: 'MMSE 평균 향상' },
                      { num: '<실제 데이터로 교체>', label: '환자 만족도' },
                      { num: '<실제 데이터로 교체>', label: '표본 수' },
                    ].map((m) => (
                      <div
                        key={m.label}
                        style={{
                          padding: 14,
                          background: 'var(--surface-1)',
                          borderRadius: 10,
                          border: '1px solid var(--line)',
                        }}
                      >
                        <div
                          className="num"
                          style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)', lineHeight: 1, marginBottom: 6 }}
                        >
                          {m.num}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.03em' }}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    background: 'var(--surface-1)',
                    border: '1px solid var(--line)',
                    borderRadius: 16,
                    padding: 32,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--accent)',
                      marginBottom: 16,
                    }}
                  >
                    — REGULATION
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, lineHeight: 1.4, color: 'var(--ink)' }}>
                    분류 코드 및<br />운영 기준
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20 }}>
                    HIRA 기존기술 코드 체계 안에서 운영
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {[
                      { label: '코드', val: 'MZ009' },
                      { label: '분류', val: '전산화 인지재활치료' },
                      { label: '대상', val: '주의·기억' },
                    ].map((c) => (
                      <div
                        key={c.label}
                        style={{
                          padding: '10px 14px',
                          background: 'var(--surface-3)',
                          borderRadius: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--ink-2)',
                        }}
                      >
                        {c.label}{' '}
                        <span className="num" style={{ color: 'var(--accent)' }}>
                          {c.val}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p style={{ marginTop: 20, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                    삼성서울병원 신경과 30년 임상 자산이 뒷받침하는 솔루션. 상세 운영 가이드는 아래{' '}
                    <em>운영 시 참고 정보</em> 섹션을 확인하세요.
                  </p>
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 핵심 기능 ──────────────────────────────────────────────────── */}
        <section style={{ padding: '100px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            <SectionBlock label="Features" title="핵심 기능">
              <div className="cols-3" style={{ gap: 20, marginTop: 56 }}>
                {[
                  { icon: '✓', title: '환자 맞춤 난이도 조정', desc: '인지 상태에 따라 매 세션 자동 조정. 너무 쉽거나 어렵지 않은 적정 부하 유지.' },
                  { icon: '⊕', title: '주의·기억 중심 영역', desc: 'MZ009 분류 기준에 부합. 주의력·기억력·실행 기능 훈련 모듈.' },
                  { icon: '⌖', title: '진척도 자동 기록', desc: '매 세션 점수·반응속도가 의사 대시보드에 자동 누적.' },
                  { icon: '⌕', title: 'SCST·MMSE 결과 자동 연동', desc: '이전 검사 결과를 자동으로 가져와 중재 시작 시간 단축.' },
                  { icon: '⚐', title: '가정용 PASCAL-Home 연동', desc: 'PASCAL-Med 사용 환자에게 가정용 PASCAL-Home(비의료기기 앱) 안내. 환자·보호자가 직접 구독.' },
                  { icon: '⌘', title: '표준 영수증 자동 생성', desc: 'MZ009 운영 시 필요한 영수증 양식 자동 생성.' },
                ].map((f) => (
                  <div
                    key={f.title}
                    style={{
                      background: 'var(--surface-1)',
                      border: '1px solid var(--line)',
                      borderRadius: 16,
                      padding: 24,
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: 'rgba(245,158,11,0.1)',
                        color: 'var(--amber-fg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 18,
                        marginBottom: 16,
                      }}
                    >
                      {f.icon}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: 'var(--ink)' }}>
                      {f.title}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{f.desc}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 운영 시 참고 정보 — MZ009 절제된 톤 ──────────────────────── */}
        <section style={{ padding: '100px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            <div
              style={{
                background: 'var(--surface-1)',
                border: '1px solid var(--line)',
                borderRadius: 20,
                padding: 48,
              }}
            >
              <div style={{ marginBottom: 32, paddingBottom: 28, borderBottom: '1px solid var(--line)' }}>
                <div
                  style={{
                    display: 'inline-block',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--ink-3)',
                    padding: '5px 12px',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--line)',
                    borderRadius: 4,
                    marginBottom: 16,
                  }}
                >
                  For Practice Operations · 운영 시 참고
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 10, color: 'var(--ink)' }}>
                  MZ009 코드 체계 안에서의 운영
                </div>
                <div style={{ color: 'var(--ink-3)', fontSize: 14, lineHeight: 1.7, maxWidth: 680 }}>
                  PASCAL-Med는 HIRA 기존기술 MZ009(전산화 인지재활치료) 분류 안에서 운영됩니다.
                  의료기관의 일반적인 비급여 진료 운영 절차를 따르며, 상세한 코드 체계 활용 방식은
                  계약 후 제공되는 운영 가이드를 참고하시면 됩니다.
                </div>
              </div>

              <div className="cols-2" style={{ gap: 40 }}>
                {[
                  {
                    title: '코드 분류 정보',
                    items: ['HIRA 코드: MZ009', '분류명: 전산화 인지재활치료', '대상 영역: 주의·기억', '운영 형태: 비급여 진료'],
                  },
                  {
                    title: '운영 안내',
                    items: [
                      '의료기관별 자율 책정에 따라 운영',
                      '표준 영수증 양식 자동 생성',
                      '의료기관 비급여 진료비 고지 의무 준수 지원',
                      '회계 처리 가이드 별도 제공',
                    ],
                  },
                ].map((sec) => (
                  <div key={sec.title}>
                    <h4
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        marginBottom: 14,
                        color: 'var(--ink)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          width: 4,
                          height: 16,
                          background: '#f59e0b',
                          borderRadius: 2,
                          display: 'inline-block',
                        }}
                      />
                      {sec.title}
                    </h4>
                    <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {sec.items.map((item) => (
                        <li
                          key={item}
                          style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, paddingLeft: 16, position: 'relative' }}
                        >
                          <span style={{ position: 'absolute', left: 0, top: 0, color: 'var(--amber-fg)', fontWeight: 700 }}>·</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: 'var(--surface-1)',
                  borderRadius: 10,
                  fontSize: 12,
                  color: 'var(--ink-3)',
                  lineHeight: 1.6,
                  border: '1px solid var(--line)',
                }}
              >
                ※ 본 정보는 운영 참고용입니다. 실제 단가·청구·세무 처리는 의료기관의 자율적 판단에 따르며,
                법적 책임은 해당 의료기관에 있습니다. 상세 운영 가이드는 계약 의료기관에게 별도 제공됩니다.
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ────────────────────────────────────────────────────────── */}
        <section style={{ padding: '100px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            <SectionBlock label="FAQ" title="자주 묻는 질문">
              <div style={{ maxWidth: 800, margin: '56px auto 0' }}>
                {[
                  {
                    q: '치매 환자에게도 사용 가능한가요?',
                    a: '경도인지장애(MCI)와 초기·중기 치매 환자가 주 대상입니다. 환자 인지 상태에 따라 난이도가 자동 조정되며, 중증 치매의 경우 의사 판단에 따라 더 단순한 모듈을 선택하실 수 있습니다.',
                  },
                  {
                    q: '의료기관에 별도 태블릿이 필요한가요?',
                    a: '자사 권장 태블릿 모델을 별도 구매 가능하며, 기존 보유 iPad/Android 태블릿으로도 사용 가능합니다.',
                  },
                  {
                    q: 'SCST·MMSE 결과를 자동으로 가져올 수 있나요?',
                    a: '네. 동일 환자의 BeauBrain 검사 기록이 자동 연동됩니다. 환자별 인지 영역 점수를 보고 적합한 훈련 패키지를 선택하실 수 있습니다.',
                  },
                  {
                    q: 'PASCAL-Home은 어떻게 연결되나요?',
                    a: 'PASCAL-Med 사용 환자에게 가정용 PASCAL-Home(비의료기기 웰니스 앱)을 안내합니다. PASCAL-Home은 환자 또는 보호자가 직접 BeauBrain에 구독하며, 의료기관이 진료비로 청구하지 않습니다. 환자가 가정에서 인지 활동을 이어가고, 재방문 시 PASCAL-Med로 재검사하여 변화를 추적하는 흐름입니다.',
                  },
                  {
                    q: '환자 데이터 보안은?',
                    a: '개인정보보호법·의료법을 준수하며, 전송·저장 시 모두 암호화, 접근 로그 기록 등을 적용합니다.',
                  },
                ].map((item) => (
                  <div
                    key={item.q}
                    style={{ borderBottom: '1px solid var(--line)', padding: '20px 0' }}
                  >
                    <div
                      style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', color: 'var(--ink)' }}
                    >
                      <span
                        className="num"
                        style={{ color: 'var(--accent)', fontWeight: 700, marginRight: 14 }}
                      >
                        Q.
                      </span>
                      {item.q}
                    </div>
                    <div
                      style={{ marginTop: 10, color: 'var(--ink-3)', fontSize: 14, lineHeight: 1.7, paddingLeft: 32 }}
                    >
                      {item.a}
                    </div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── CTA ────────────────────────────────────────────────────────── */}
        <section
          style={{
            padding: '100px 0',
            textAlign: 'center',
            borderTop: '1px solid var(--line)',
            background: 'var(--surface-1)',
          }}
        >
          <div className="container">
            <h2 className="cta-title" style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: 16, letterSpacing: '-0.03em' }}>
              진단 이후의 답을 찾으셨다면.
            </h2>
            <p style={{ color: 'var(--ink-3)', marginBottom: 36, fontSize: 17 }}>
              한 번의 문의로 진료 전 과정의 도구가 함께 열립니다.
            </p>
            <div className="cta-row">
              <a href={INQUIRY_HREF} target="_blank" rel="noopener noreferrer" className="btn-primary btn-lg">
                도입 문의하기 →
              </a>
              <a href="/credit" className="btn-outline btn-lg">
                통합 크레딧 알아보기
              </a>
            </div>
          </div>
        </section>

      </SolutionLayout>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/solutions/pascal-med')({
  component: Page,
});
